/**
 * Landmark asset loader + substitution layer (issue #124).
 *
 * A minimal loader keyed by OSM way id (the LANDMARK_TABLE in
 * landmarks.ts). Where a footprint has a committed glTF asset, it is fetched
 * LAZILY (off the boot path, like the #130 minimap/postprocessing chunks) and
 * rendered IN PLACE OF the procedural building — never in addition. The
 * procedural building is the complete fallback: a missing/failed/disabled asset,
 * or an assetless table entry (the school today), leaves the procedural massing
 * rendering with one structured console warning and no boot failure (hard
 * constraint #7).
 *
 * Scene furniture only (hard constraint #1): a landmark asset is decoration over
 * a real footprint and has no bearing on any LOS result — this module never
 * imports from raypath/ (guarded by buildings.guard.test.ts). Ground elevation
 * comes only from the terrain collider (hard constraint #6): each seated asset
 * self-heals onto streamed ground on the tile tick, the same contract as
 * BuildingsLayer, and stays hidden (never floating/buried) until its terrain
 * arrives.
 *
 * The asset carries zero textures / zero photo pixels (the #105 ToS boundary);
 * this loader adds none.
 */
import { Group, type Object3D } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { GroundSampler } from '../camera/groundLock';
import type { LocalXZ } from '../core/coordinates';
import { loadLazy } from '../core/lazy';
import {
  LANDMARK_TABLE,
  footprintToLocalXZ,
  landmarkPlacement,
  type LandmarkEntry,
  type LandmarkPlacement,
} from './landmarks';

/** The minimal building shape the loader needs: id + footprint (a Building/
 * BuildingWithAppearance satisfies this structurally). */
export interface LandmarkBuilding {
  id: string;
  footprint_bng: [number, number][];
}

/** What LandmarksLayer needs from the procedural buildings layer: claim or
 * release a footprint so the two never double-render. BuildingsLayer implements
 * this; a test can stub it. */
export interface FootprintSuppressor {
  hasBuilding(id: string): boolean;
  setLandmarkSuppressed(id: string, suppressed: boolean): boolean;
}

/** A loaded glTF, narrowed to the one field this layer reads (GLTFLoader's
 * `loadAsync` resolves a superset). Injectable so tests mock the fetch/parse. */
export interface LoadedGltf {
  scene: Object3D;
}
export type GltfLoadFn = (url: string) => Promise<LoadedGltf>;

interface LandmarkAssetEntry {
  wayId: string;
  group: Group;
  centroid: LocalXZ;
  resolved: boolean;
  attemptGeneration: number;
}

/** One structured warning line (the viewer has no logger — console only), shaped
 * like the other scene-furniture warnings so it is greppable. */
function warn(reason: string, detail: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.warn(JSON.stringify({ event: 'landmark', reason, ...detail }));
}

function defaultGltfLoader(): GltfLoadFn {
  const loader = new GLTFLoader();
  return (url) => loader.loadAsync(url) as Promise<LoadedGltf>;
}

export class LandmarksLayer {
  readonly group = new Group();
  private readonly buildings: Map<string, LandmarkBuilding>;
  private readonly suppressor: FootprintSuppressor;
  private readonly loadGltf: GltfLoadFn;
  private readonly assetsEnabled: boolean;
  private readonly table: readonly LandmarkEntry[];
  private readonly entries: LandmarkAssetEntry[] = [];
  /** Synthetic negative generations when update() is called without one — mirrors
   * BuildingsLayer so each bare call behaves like a fresh generation. */
  private fallbackGeneration = 0;
  private started = false;

  /**
   * @param buildings   the footprints (id + footprint_bng) landmark ids resolve against
   * @param suppressor  the procedural buildings layer, to suppress a claimed footprint
   * @param options.loadGltf       glTF fetch/parse (defaults to a real GLTFLoader); injected in tests
   * @param options.assetsEnabled  false on a quality tier that drops rich assets (#17) — every footprint then renders procedurally
   * @param options.table          the landmark table (defaults to LANDMARK_TABLE)
   */
  constructor(
    buildings: LandmarkBuilding[],
    suppressor: FootprintSuppressor,
    options: { loadGltf?: GltfLoadFn; assetsEnabled?: boolean; table?: readonly LandmarkEntry[] } = {},
  ) {
    this.group.name = 'landmarks';
    this.buildings = new Map(buildings.map((b) => [b.id, b]));
    this.suppressor = suppressor;
    this.loadGltf = options.loadGltf ?? defaultGltfLoader();
    this.assetsEnabled = options.assetsEnabled ?? true;
    this.table = options.table ?? LANDMARK_TABLE;
  }

  /** Loaded (parsed) landmark asset count — assets present in the scene graph. */
  get loadedCount(): number {
    return this.entries.length;
  }

  /** Loaded assets still waiting on streamed ground before they are shown. */
  get unresolvedCount(): number {
    return this.entries.filter((e) => !e.resolved).length;
  }

  /**
   * Kicks off the lazy asset loads (off the boot path). Runs a SYNCHRONOUS pass
   * first — before any await — that claims every asset-backed footprint via the
   * suppressor, so the procedural building is suppressed before it can flash, and
   * flags orphan ids / assetless / tier-dropped entries (all of which leave the
   * procedural building rendering). It then loads each claimed asset and, on any
   * failure, un-suppresses that footprint so the procedural building is the
   * fallback. Idempotent (a second call no-ops). Awaitable so tests can settle
   * every attempt; boot fires-and-forgets it.
   */
  async loadAssets(sampleGround?: GroundSampler): Promise<void> {
    if (this.started) return;
    this.started = true;

    const pending: Array<{ entry: LandmarkEntry; placement: LandmarkPlacement }> = [];
    for (const entry of this.table) {
      const building = this.buildings.get(entry.wayId);
      if (!building) {
        // Orphan: an asset id with no footprint in buildings.json. Name the id,
        // render nothing at a guessed position (failure-table). An assetless
        // orphan (shouldn't happen) is silent — there is nothing to place.
        if (entry.asset != null) warn('orphan-id', { wayId: entry.wayId, label: entry.label });
        continue;
      }
      const placement = landmarkPlacement(footprintToLocalXZ(building.footprint_bng), { flip: entry.flip });
      if (!placement) {
        // Degenerate footprint: fall back procedurally rather than seat a guess.
        warn('degenerate-footprint', { wayId: entry.wayId, label: entry.label });
        continue;
      }
      if (entry.asset == null) {
        // Assetless footprint (the school): procedural fallback is expected here,
        // not a failure — no warning.
        continue;
      }
      if (!this.assetsEnabled) {
        // Quality-tier drop (#17): skip the asset; the procedural building renders
        // rather than nothing. Info, not a warning — it is a deliberate downgrade.
        // eslint-disable-next-line no-console
        console.info(JSON.stringify({ event: 'landmark', reason: 'assets-disabled-tier', wayId: entry.wayId }));
        continue;
      }
      // Claim the footprint NOW (synchronous, pre-await) so the asset and the
      // procedural building never render together — substitution, not addition.
      this.suppressor.setLandmarkSuppressed(entry.wayId, true);
      pending.push({ entry, placement });
    }

    await Promise.all(pending.map((p) => this.loadOne(p.entry, p.placement, sampleGround)));
  }

  private async loadOne(entry: LandmarkEntry, placement: LandmarkPlacement, sampleGround?: GroundSampler): Promise<void> {
    const url = entry.asset as string; // `pending` only ever holds asset-backed entries
    const res = await loadLazy(() => this.loadGltf(url));
    if (!res.ok) {
      // Missing file / glTF parse error: restore the procedural building as the
      // fallback (no gap), one structured warning, no throw — every other
      // landmark and the whole buildings layer keep working (hard constraint #7).
      warn('asset-load-failed', { wayId: entry.wayId, url, error: res.error.message });
      this.suppressor.setLandmarkSuppressed(entry.wayId, false);
      return;
    }

    const group = new Group();
    group.name = `landmark-${entry.wayId}`;
    group.rotation.y = placement.yawRad;
    group.position.set(placement.centroid.x, 0, placement.centroid.z);
    group.visible = false; // shown only once ground-seated (hard constraint #6)
    group.add(res.value.scene);
    group.traverse((o) => {
      const mesh = o as { isMesh?: boolean; castShadow?: boolean; receiveShadow?: boolean };
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });
    this.group.add(group);

    const assetEntry: LandmarkAssetEntry = {
      wayId: entry.wayId,
      group,
      centroid: placement.centroid,
      resolved: false,
      attemptGeneration: Number.NaN,
    };
    this.entries.push(assetEntry);
    // Seat immediately if the ground under it is already streamed; otherwise the
    // tile tick heals it (never leaving it floating/buried).
    if (sampleGround) this.seat(assetEntry, sampleGround);
  }

  private seat(e: LandmarkAssetEntry, sampleGround: GroundSampler): boolean {
    const ground = sampleGround(e.centroid.x, e.centroid.z);
    if (ground === null) return false;
    e.group.position.y = ground;
    e.group.visible = true;
    e.resolved = true;
    return true;
  }

  /**
   * Self-heal grounding on the tile-update tick (same contract as
   * BuildingsLayer.update): re-seat any loaded-but-unresolved landmark against
   * the current ground sampler, at most once per generation, up to `maxSamples`
   * this call. Returns true if the budget ran out with work remaining. A landmark
   * whose terrain has not streamed stays hidden until it does.
   */
  update(sampleGround: GroundSampler, maxSamples = Infinity, generation?: number): boolean {
    const gen = generation ?? (this.fallbackGeneration -= 1);
    let samples = 0;
    for (const e of this.entries) {
      if (e.resolved || e.attemptGeneration === gen) continue;
      if (samples >= maxSamples) return true;
      samples += 1;
      e.attemptGeneration = gen;
      this.seat(e, sampleGround);
    }
    return false;
  }

  /** Buildings-toggle mirror: the landmark church/school ARE buildings, so they
   * follow the same visibility toggle as the procedural buildings layer. */
  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }
}
